import {
  Modal,
  BlockStack,
  InlineStack,
  TextField,
  Checkbox,
  Popover,
  ActionList,
  Button,
  Text,
} from "@medentic/polaris-react";
import { ViewIcon, LockIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useCallback, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@app/store/store";
import { EventTicket, TicketFormData } from "@features/events/types";
import { GenerativeTextField } from "@shared/ui";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { DatePickerField } from "@shared/ui";
import { useTicketValidation, TouchedFields } from "./useTicketValidation";
import { useTaxCalculator } from "@shared/hooks/common/useTaxCalculator";

const VISIBILITY_ICON_MAP = {
  0: LockIcon,
  1: ViewIcon,
} as const;

interface TicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (ticketData: TicketFormData) => void;
  editingTicket: EventTicket | null;
  formData: TicketFormData;
  onFormChange: (
    field: keyof TicketFormData,
    value: string | number | boolean
  ) => void;
  onMultilingualChange: (
    field: "title" | "description",
    value: Record<string, string>
  ) => void;
  eventCurrency: string;
  loading?: boolean;
}

export default function TicketModal({
  isOpen,
  onClose,
  onSave,
  editingTicket,
  formData,
  onFormChange,
  onMultilingualChange,
  eventCurrency,
  loading = false,
}: TicketModalProps) {
  const { t } = useTranslation();
  const { visibilityOptions: metadataVisibilityOptions } = useEventMetadata();
  const [popoverActive, setPopoverActive] = useState(false);
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const { errors, validate, clearErrors } = useTicketValidation();
  const { calculateTax, taxRate } = useTaxCalculator();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );

  // Calculate total price with tax (price is VAT exclusive)
  const taxCalculation = useMemo(() => {
    const price = formData.isFree ? 0 : (formData.price || 0);
    return calculateTax(price, eventCurrency, false);
  }, [formData.price, formData.isFree, eventCurrency, calculateTax]);

  const togglePopover = useCallback(() => setPopoverActive((active) => !active), []);

  const handleModalClose = useCallback(() => {
    clearErrors();
    setTouchedFields({});
    onClose();
  }, [clearErrors, onClose]);

  // Visibility options with fallback
  const visibilityOptions = useMemo(() => {
    if (metadataVisibilityOptions.length === 0) {
      return [
        { 
          label: t("eventDetails.visibility.private"), 
          value: "0",
          description: t("eventDetails.visibility.privateDescription"),
          icon: VISIBILITY_ICON_MAP[0]
        },
        { 
          label: t("eventDetails.visibility.public"), 
          value: "1",
          description: t("eventDetails.visibility.publicDescription"),
          icon: VISIBILITY_ICON_MAP[1]
        },
      ];
    }
    return metadataVisibilityOptions;
  }, [metadataVisibilityOptions, t]);

  // Get current selection for button display
  const currentSelection = useMemo(() => {
    const currentValue = formData.visibility?.toString() || "0";
    return visibilityOptions.find(option => option.value === currentValue) || visibilityOptions[0];
  }, [formData.visibility, visibilityOptions]);

  // Handle save
  const handleSave = useCallback(() => {
    // Run form validation - inline errors will be shown
    if (!validate(formData, selectedLanguages, !!editingTicket, touchedFields)) {
      return;
    }

    onSave(formData);
  }, [formData, onSave, validate, selectedLanguages, editingTicket, touchedFields]);

  return (
    <Modal
      open={isOpen}
      onClose={handleModalClose}
      title={
        editingTicket
          ? t("eventDetails.tickets.editTicket")
          : t("eventDetails.tickets.createTicket")
      }
      primaryAction={{
        content: editingTicket
          ? t("common.update")
          : t("eventDetails.tickets.addTicket"),
        onAction: handleSave,
        loading,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleModalClose,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Basic Information Section */}
          <BlockStack gap="100">
            <GenerativeTextField
              id="ticket-name-bg"
              placementId="event.ticket.title"
              label={t("eventDetails.tickets.name")}
              value={formData.title ?? {}}
              onChange={(value) => onMultilingualChange("title", value)}
              type="textField"
              canGenerate={true}
              requiredIndicator
              validateAllLanguages={true}
              error={errors.title}
            />
          </BlockStack>

          <BlockStack gap="100">
            <GenerativeTextField
              id="ticket-description-bg"
              placementId="event.ticket.description"
              label={t("eventDetails.tickets.description")}
              value={formData.description ?? {}}
              onChange={(value) => onMultilingualChange("description", value)}
              type="textArea"
              canGenerate={true}
            />
          </BlockStack>

          <BlockStack gap="100">
            <Text
              as="p"
              variant="bodyMd"
              requiredIndicator
            >
              {t("eventDetails.tickets.visibility")}
            </Text>
            <Popover
              active={popoverActive}
              activator={
                <Button 
                  onClick={togglePopover} 
                  disclosure
                  fullWidth
                  size="large"
                  icon={currentSelection.icon}
                >
                  {currentSelection.label}
                </Button>
              }
              autofocusTarget="first-node"
              onClose={togglePopover}
            >
              <ActionList
                actionRole="menuitem"
                sections={[
                  {
                    title: t("eventDetails.tickets.visibility"),
                    items: visibilityOptions.map((option) => ({
                      content: option.label,
                      helpText: option.description,
                      icon: option.icon,
                      onAction: () => {
                        onFormChange("visibility", parseInt(option.value) || 0);
                        setPopoverActive(false);
                      },
                    })),
                  },
                ]}
              />
            </Popover>
          </BlockStack>

          {/* Pricing Section */}
          <Checkbox
            label={t("eventDetails.tickets.freeTicket")}
            checked={formData.isFree}
            onChange={(checked) => onFormChange("isFree", checked)}
          />

          <InlineStack gap="400">
            <BlockStack gap="100">
              <TextField
                type="number"
                label={t("eventDetails.tickets.price")}
                value={
                  Number.isNaN(formData.price) ||
                  (!editingTicket && !touchedFields.price && formData.price === 0)
                    ? ""
                    : formData.price.toString()
                }
                onChange={(value) => {
                  setTouchedFields((prev) => ({ ...prev, price: true }));
                  onFormChange("price", parseFloat(value));
                }}
                suffix={eventCurrency}
                autoComplete="off"
                requiredIndicator
                disabled={formData.isFree}
                error={errors.price}
                helpText={t("eventDetails.tickets.priceVatExclusive")}
              />
              {!formData.isFree && formData.price > 0 && (
                <Text variant="bodySm" as="span" tone="subdued">
                  {t("eventDetails.tickets.withTax", {
                    price: taxCalculation.totalPriceLabel,
                    tax: taxRate,
                  })}
                </Text>
              )}
            </BlockStack>

            <TextField
              type="number"
              label={t("eventDetails.tickets.minQuantity")}
              value={
                Number.isNaN(formData.minQuantity) ||
                (!editingTicket && !touchedFields.minQuantity && formData.minQuantity === 1)
                  ? ""
                  : formData.minQuantity.toString()
              }
              onChange={(value) => {
                setTouchedFields((prev) => ({ ...prev, minQuantity: true }));
                onFormChange("minQuantity", parseInt(value));
              }}
              autoComplete="off"
              requiredIndicator
              error={errors.minQuantity}
            />
          </InlineStack>

          {/* Availability Section */}
          <Checkbox
            label={t("eventDetails.tickets.ticketStart")}
            checked={formData.hasStart}
            onChange={(checked) => onFormChange("hasStart", checked)}
          />

          {formData.hasStart && (
            <DatePickerField
              label={t("eventDetails.tickets.startDate")}
              value={formData.startsAt ?? ""}
              onChange={(value) => onFormChange("startsAt", value)}
              isRequired={true}
              error={errors.startsAt}
            />
          )}

          <Checkbox
            label={t("eventDetails.tickets.ticketExpire")}
            checked={formData.hasExpiry}
            onChange={(checked) => onFormChange("hasExpiry", checked)}
          />

          {formData.hasExpiry && (
            <DatePickerField
              label={t("eventDetails.tickets.expiryDate")}
              value={formData.expiresAt ?? ""}
              onChange={(value) => onFormChange("expiresAt", value)}
              isRequired={true}
              error={errors.expiresAt}
            />
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
